import {
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';

import * as api from '../lib/api';
import type { PaginatedItems } from '../lib/api-client';
import { useAuth } from '../providers/AuthProvider';
import { type Notification } from '../types';

interface NotificationsResponse extends PaginatedItems<Notification> {
  unreadCount: number;
  hasMore: boolean;
}

interface NotificationCountResponse {
  count: number;
}

export const NOTIFICATIONS_PAGE_SIZE = 5;
export const notificationsQueryKey: readonly [string, number] = ['notifications', NOTIFICATIONS_PAGE_SIZE];

export function useNotifications() {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  // Fetch notifications with TanStack Query
  const { data, error, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, refetch } = useInfiniteQuery<
    NotificationsResponse,
    Error,
    InfiniteData<NotificationsResponse, number>,
    readonly [string, number],
    number
  >({
    queryKey: notificationsQueryKey,
    queryFn: ({ pageParam }) => api.getNotifications(pageParam, NOTIFICATIONS_PAGE_SIZE),
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      return lastPage.hasMore ? lastPageParam + 1 : undefined;
    },
    initialPageParam: 0,
    refetchOnWindowFocus: true,
  });

  // Get unread notification count
  const { data: countData } = useQuery<NotificationCountResponse>({
    queryKey: ['/notifications/count'],
    queryFn: () => api.getNotificationCount(),
    refetchOnWindowFocus: true,
    placeholderData: { count: 0 },
  });

  // Helper function to refresh notification data
  const refreshNotifications = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ['/notifications/count'] });
  };

  // Mark notification as read
  const markAsReadMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!isAuthenticated) return false;
      await api.markNotificationAsRead(id);
    },
    onSuccess: () => {
      refreshNotifications();
    },
  });

  // Mark all as read
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!isAuthenticated) return false;
      await api.markAllNotificationsAsRead();
    },
    onSuccess: () => {
      refreshNotifications();
    },
  });

  const loadMore = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  const allNotifications = data?.pages.flatMap((pageData) => pageData.items) || [];

  return {
    notifications: allNotifications,
    unreadCount: countData?.count || 0,
    isLoading: isLoading,
    isFetchingMore: isFetchingNextPage,
    error,
    markAsRead: markAsReadMutation.mutateAsync,
    markAllAsRead: markAllAsReadMutation.mutateAsync,
    isMarkingAll: markAllAsReadMutation.isPending,
    loadMore,
    hasMore: hasNextPage ?? false,
    refreshNotifications,
  };
}

export function handleNotificationCreated(queryClient: QueryClient, notification: Notification) {
  queryClient.setQueryData<InfiniteData<NotificationsResponse, number>>(notificationsQueryKey, (oldData) => {
    if (!oldData) {
      return oldData;
    }
    const [firstPage, ...restPages] = oldData.pages;

    return {
      ...oldData,
      pages: [
        {
          ...firstPage,
          items: [notification, ...firstPage.items],
        },
        ...restPages,
      ],
    };
  });

  if (!notification.isRead) {
    queryClient.setQueryData<NotificationCountResponse>(['/notifications/count'], (oldCountData) => {
      return { count: (oldCountData?.count || 0) + 1 };
    });
  }
}
